import React, { useState, useMemo } from "react";
import type { NodeVariable, Variable } from "../../types";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import { Icon } from "@elaniintc/goes-design-system";
import type {
  IDropdownListItem,
  DropdownListGroup,
} from "@/components/ui/dropdown-list/dropdown-list";
import { nodeVariableToGroups } from "../../types";

export interface VariableSelectorProps {
  variables: NodeVariable[];
  selectedVariable: Variable | null;
  onSelectVariable: (variable: Variable | null) => void;
  triggerGroup?:
    | "middle"
    | "middle-left"
    | "middle-right"
    | "down-left"
    | "down-right"
    | "up-left"
    | "up-right"
    | "left"
    | "right"
    | "down";
  placeholder?: string;
  filterByType?: string; // Filtrar variables por tipo específico
}

export const VariableSelector = ({
  variables,
  selectedVariable,
  onSelectVariable,
  triggerGroup: group,
  placeholder = "Elegir variable",
  filterByType,
}: VariableSelectorProps) => {
  console.log(selectedVariable);
  const [variableSearch, setVariableSearch] = useState("");
  const [_selectedVariable, setSelectedVariable] =
    useState<IDropdownListItem>();
  const [_selectedGroup, setSelectedGroup] = useState<DropdownListGroup>();
  // Inicializar loading como true si hay una variable seleccionada
  const [isLoading, setIsLoading] = useState(!!selectedVariable);

  const variableGroups = useMemo(() => {
    const groups = nodeVariableToGroups(variables);
    const getAllowedTypes = (type: string) => {
      switch (type) {
        case "text":
          return ["string", "number"];
        case "number":
          return ["number"];
        case "date":
          return ["date"];
        case "boolean":
          return ["boolean"];
        default:
          return ["string", "number", "date", "boolean"];
      }
    };

    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => {
          const typeMatch = filterByType
            ? getAllowedTypes(filterByType).includes(item.detail || "")
            : true;
          const searchMatch =
            (item.label as string)
              .toLowerCase()
              .includes(variableSearch.toLowerCase()) ||
            group.groupLabels.some((label) =>
              label.toLowerCase().includes(variableSearch.toLowerCase()),
            );
          return typeMatch && searchMatch;
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [variables, variableSearch, filterByType]);

  // Sincronizar variable seleccionada cuando selectedVariable cambie
  React.useEffect(() => {
    if (selectedVariable && variableGroups.length > 0) {
      // Buscar la variable en los grupos
      let found = false;
      for (const group of variableGroups) {
        const variable = group.items.find(
          (item) => item.value === selectedVariable.id,
        );
        if (variable) {
          setSelectedVariable(variable);
          setSelectedGroup(group);
          setIsLoading(false);
          found = true;
          break;
        }
      }

      // Si hay una variable seleccionada pero no se encontró en los grupos, mostrar loading
      if (!found) {
        setIsLoading(true);
      }
    } else if (!selectedVariable) {
      // No hay variable seleccionada
      setSelectedVariable(undefined);
      setSelectedGroup(undefined);
      setIsLoading(false);
    } else if (selectedVariable && variableGroups.length === 0) {
      // Hay variable seleccionada pero no hay grupos aún (aún cargando)
      setIsLoading(true);
    }
  }, [selectedVariable, variableGroups]);

  const onVariableSelection = (variableId: string, groupId: string) => {
    const group = variableGroups.find((g) => g.groupId === groupId);
    const variable = group?.items.find((item) => item.value === variableId);

    if (variable) {
      // Convertir de IDropdownListItem a Variable
      const selectedVar: Variable = {
        id: variable.value,
        name: variable.label as string,
        type: variable.detail as Variable["type"],
        data: variable.icon,
      };
      onSelectVariable(selectedVar);
    } else {
      onSelectVariable(null);
    }

    setSelectedVariable(variable);
    setSelectedGroup(group);
  };

  return isLoading ? (
    <button
      disabled
      className="font-semibold whitespace-nowrap transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none flex justify-start items-center group min-w-0 relative gap-2 border-neutral text-body-loud placeholder:text-body-quiet disabled:bg-surface disabled:text-on-disabled disabled:cursor-not-allowed px-3 py-2 h-11 text-base leading-3xl border border-l-transparent rounded-r-lg flex-1"
    >
      <Icon
        type="far"
        name="spinner-third"
        className="animate-spin text-body-quiet"
      />
      <p className="truncate font-normal text-body-quiet">
        Cargando variable...
      </p>
    </button>
  ) : (
    <DropdownList
      group={group}
      options={variableGroups}
      selectedValue={_selectedVariable}
      selectedGroup={_selectedGroup}
      toggleSelection={(variable, group) => {
        onVariableSelection(variable.value, group?.groupId || "");
      }}
      widthClassName="flex-1"
      placeholder={placeholder}
      search={{
        placeholder: "Buscar variable",
        onSearch: (query) => {
          setVariableSearch(query);
        },
        query: variableSearch,
      }}
    />
  );
};
